import { useMe } from "@/api/services/main/auth";
import { usePublishedMasterProducts } from "@/api/services/main/master-product";
import { useMyProductContracts } from "@/api/services/main/product-contract";
import { IconChevronRight, IconIrohanaSymbol } from "@/assets/icons";
import { InnerBox } from "@/components/elements/InnerBox";
import { Table } from "@/components/elements/Table";
import { FirstLoginModal } from "@/features/dashboard/components/FirstLoginModal";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { button, text } from "@/theme/colors";
import { toNumber } from "@/utils/number";
import { createUrlWithParams, openNewTab } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
type TProductRow = {
  id: number | string;
  productName: string;
  statusLabel: string;
  notContracted?: boolean;
  actionLabel: string;
  actionSubLabel?: string;
  actionHref?: string;
  /** When true, the action mints an RSO OAuth redirect on click instead of using a static href. */
  requiresMint?: boolean;
  /** When true, the action opens the first-login setup modal on click instead of navigating. */
  requiresInitSetup?: boolean;
  /** When true, the action opens the (admin-set, possibly external) introduction page instead of the product. */
  requiresIntro?: boolean;
  productCode: string;
  /** Logo image path from master data; null falls back to the hard-coded irohana symbol. */
  logo: string | null;
};

/** A row is clickable when it mints an RSO redirect, has a real product href, or points at an introduction page. */
const isProductNavigable = (row: TProductRow) =>
  Boolean(row.requiresMint) || (Boolean(row.requiresIntro) && Boolean(row.actionHref)) || (Boolean(row.actionHref) && row.actionHref !== "#");

/**
 * Enter the product: mint a fresh RSO OAuth redirect, open the introduction page, or open the static href.
 * Product hand-offs reuse the single shared per-product tab; the introduction page is an external link.
 */
const navigateToProduct = (row: TProductRow) => {
  if (row.requiresMint) {
    openNewTab(createUrlWithParams(AppRoutes.mintOauthRedirect, { productCode: row.productCode }), `product:${row.productCode}`);
    return;
  }
  if (row.requiresIntro) {
    openNewTab(row.actionHref, `product-introduce:${row.productCode}`);
    return;
  }
  if (row.actionHref) {
    openNewTab(row.actionHref, `product-action:${row.productCode}`);
  }
};

/** Product logo from master data; falls back to the hard-coded irohana symbol when absent or the image fails to load. */
const ProductLogo: FC<{ logo: string | null }> = ({ logo }) => {
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    return <IconIrohanaSymbol width={28} height={28} />;
  }

  return <img src={logo} alt="" width={172} onError={() => setFailed(true)} />;
};

export const ProductContract: FC = () => {
  const { data: contracts, isLoading } = useMyProductContracts();
  const { data: masterProducts } = usePublishedMasterProducts();
  const { data: me } = useMe();

  const rows = useMemo<TProductRow[]>(() => {
    const contractRows = contracts
      .map((contract) => {
        let actionInfo: Pick<TProductRow, "actionLabel" | "actionSubLabel" | "actionHref" | "requiresMint" | "requiresInitSetup"> = {
          actionLabel: "",
          actionSubLabel: "",
          actionHref: "",
        };
        const meContracts = me?.data.productContracts ?? [];
        const isGrantable = () => {
          if (!meContracts.length) return false;
          return meContracts.some((pc) => pc.productCode === contract.product.code);
        };
        const hasProductAccount = () => {
          if (!meContracts.length) return false;
          return meContracts.some((pc) => pc.productCode === contract.product.code && pc.referenceUserId != null);
        };

        if (!isGrantable()) {
          actionInfo = {
            actionLabel: "アクセス権限なし",
            actionSubLabel: "ご契約管理者に相談ください",
          };
        } else if (!hasProductAccount()) {
          actionInfo = {
            actionLabel: "初回利用設定へ",
            requiresInitSetup: true,
          };
        } else {
          actionInfo = {
            actionLabel: "プロダクトへ",
            requiresMint: true,
          };
        }

        return {
          id: contract.id,
          productName: contract.product.name,
          statusLabel: "利用中",
          productCode: contract.product.code,
          logo: contract.product.logo ?? null,
          ...actionInfo,
          product: contract.product,
        };
      })
      .filter((contract) => {
        if (!masterProducts.length) return false;
        return masterProducts.some((mp) => toNumber(mp.productId) === toNumber(contract.product.id));
      });

    const catalogRows = masterProducts
      .filter((masterProduct) => Boolean(masterProduct.introductionPageUrl) && masterProduct.productId == null)
      .map<TProductRow>((masterProduct) => ({
        id: `master-${masterProduct.id}`,
        productName: masterProduct.name,
        statusLabel: "未契約",
        notContracted: true,
        productCode: "",
        logo: masterProduct.logo,
        actionLabel: "サービス紹介ページへ",
        actionSubLabel: "",
        actionHref: masterProduct.introductionPageUrl ?? "",
        requiresIntro: true,
      }));

    return [...contractRows, ...catalogRows];
  }, [contracts, masterProducts, me]);

  const columns = useMemo<MRT_ColumnDef<TProductRow>[]>(
    () => [
      {
        header: "プロダクト",
        id: "product",
        accessorKey: "productName",
        Cell: ({ row }) => {
          const fullName = row.original.productName;
          let productName = fullName;
          if (productName.toLowerCase().startsWith("irohana")) {
            productName = productName.slice(7);
          }
          return (
            <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
              <ProductLogo logo={row.original.logo} />
            </Stack>
          );
        },
      },
      {
        header: "契約状態",
        id: "status",
        accessorKey: "statusLabel",
        Cell: ({ row }) => (
          <Typography variant="sub16Semi" color={row.original.notContracted ? text.disable : text.primary}>
            {row.original.statusLabel}
          </Typography>
        ),
      },
      {
        header: "サービス利用",
        id: "action",
        Cell: ({ row }) => <ProductActionCell row={row.original} />,
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows,
    // Make the whole row (product name, contract status, action) navigate to the product for プロダクトへ rows.
    muiTableBodyRowProps: ({ row }) => {
      if (!isProductNavigable(row.original)) return {};
      return {
        onClick: () => navigateToProduct(row.original),
        sx: { cursor: "pointer" },
      };
    },
  });

  return (
    <InnerBox padding="24px">
      <Stack gap="24px">
        <Typography variant="head20Bold" color={text.primary}>
          ご契約中のプロダクト
        </Typography>
        <Table
          table={table}
          isLoading={isLoading}
          variant="borderless"
          bodyRowHeight={64}
          emptyDataPlaceholder="ご契約中のプロダクトはありません。"
        />
      </Stack>
    </InnerBox>
  );
};

const ProductActionCell: FC<{ row: TProductRow }> = ({ row }) => {
  const { actionLabel, actionSubLabel, actionHref, requiresMint, requiresInitSetup } = row;
  const [isFirstLoginOpen, setIsFirstLoginOpen] = useState(false);

  const isClickable = Boolean(actionHref) || Boolean(requiresMint) || Boolean(requiresInitSetup);

  const content = (
    <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" width="100%">
      <Stack gap={0.5}>
        <Typography variant="sub16Semi" color={text.primary} sx={{ textDecoration: !isClickable ? "none" : "underline" }}>
          {actionLabel}
        </Typography>
        {actionSubLabel && (
          <Typography variant="cap12" color={text.accent}>
            {actionSubLabel}
          </Typography>
        )}
      </Stack>
      <IconChevronRight fontSize={24} color={!isClickable ? text.disable : button.primary} />
    </Stack>
  );

  if (requiresInitSetup) {
    return (
      <>
        <Stack
          width="100%"
          onClick={(e) => {
            e.stopPropagation();
            setIsFirstLoginOpen(true);
          }}
          sx={{ cursor: "pointer" }}
        >
          {content}
        </Stack>
        <FirstLoginModal isOpen={isFirstLoginOpen} productContractId={row.id} onClose={() => setIsFirstLoginOpen(false)} />
      </>
    );
  }

  return content;
};
